import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import 'leaflet/dist/leaflet.css';
import React, {
  forwardRef,
  Suspense,
  useImperativeHandle,
  useRef
} from 'react';

import { useNavigationStore } from '@/stores/navigationStore';
import { calculatePostcodeBounds } from '@/utils/geojson';

import type { MapMarker, MapPolygon, MapViewProps, MapViewRef, PollingStationMarker } from './MapView';

function getBoundsFromRegion(region: MapViewProps['initialRegion']): [[number, number], [number, number]] {
  const { latitude, longitude, latitudeDelta, longitudeDelta } = region;
  return [
    [latitude - latitudeDelta / 2, longitude - longitudeDelta / 2],
    [latitude + latitudeDelta / 2, longitude + longitudeDelta / 2],
  ];
}

// Dynamically loaded map component
const LeafletMap = React.lazy(() => 
  Promise.all([
    import('leaflet'),
    import('react-leaflet')
  ]).then(([leafletModule, reactLeafletModule]) => {
    const L = leafletModule.default;
    const { MapContainer, Marker, Polygon, Popup, TileLayer, useMapEvents, Circle, CircleMarker } = reactLeafletModule;
    
    if (typeof window !== 'undefined') {
      // No marker icon patching needed, we will use custom divIcons for markers
    }

  function LeafletMapInner(
    {
      initialRegion,
      markers = [],
      pollingStationMarkers = [],
      polygons = [],
      showUserLocation = false,
      onMarkerPress,
      onPollingStationPress,
      style,
      showProgressLegend = false,
      legendItems = [],
      battlePlanId,
      postcodeSlicePolygons,
    }: MapViewProps,
    ref: React.Ref<MapViewRef>,
  ) {
    const bounds = getBoundsFromRegion(initialRegion);
    const mapRef = useRef<any>(null);
    const [showLegend, setShowLegend] = React.useState(false);
    const [showMarkers, setShowMarkers] = React.useState(false); // Start with markers hidden until zoom level is checked
    const router = useRouter();
    const { getSelectedBranch, getSelectedCampaign } = useNavigationStore();
    
    // AbortController for geolocation requests
    const geolocationAbortControllerRef = useRef<AbortController | null>(null);

    // Zoom threshold for marker visibility (Leaflet zoom levels)
    const MIN_ZOOM_FOR_MARKERS = 14; // Show markers when zoomed in to level 15 or higher

    const navigateToEngagement = (marker: MapMarker) => {
      const selectedBranch = getSelectedBranch();
      const selectedCampaign = getSelectedCampaign();
      
      if (selectedBranch && selectedCampaign && marker.engagementId && battlePlanId) {
        // Navigate to engagement details screen
        router.push({
          pathname: '/(main)/[branch]/campaign/[campaignId]/canvass/[battlePlanId]/[engagementId]',
          params: { 
            branch: selectedBranch.branch_short_name,
            campaignId: selectedCampaign.boundary_id,
            battlePlanId: battlePlanId,
            engagementId: marker.engagementId
          }
        });
      }
    };

    const handleMarkerClick = (marker: MapMarker) => {
      // Inactive route number markers should behave like polygon clicks - navigate directly
      if (marker.type === 'route-number') {
        navigateToEngagement(marker);
        return;
      }
      
      // Active route number markers and campaigner count markers use the existing onMarkerPress handler
      // The popup will be shown automatically by Leaflet
      if (marker.type === 'route-number-active' || marker.type === 'campaigner-count') {
        if (onMarkerPress) {
          onMarkerPress(marker);
        }
        return;
      }
    };

    const handlePollingStationClick = (pollingStation: PollingStationMarker) => {
      if (onPollingStationPress) {
        onPollingStationPress(pollingStation);
      }
    };

    const centerOnUserLocation = () => {
      if (typeof window === 'undefined' || !navigator.geolocation) return;
      
      // Cancel any existing geolocation request
      if (geolocationAbortControllerRef.current) {
        geolocationAbortControllerRef.current.abort();
      }
      
      // Create new AbortController for this request
      const abortController = new AbortController();
      geolocationAbortControllerRef.current = abortController;
      
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          // Check if request wasn't aborted
          if (abortController.signal.aborted) return;
          
          const { latitude, longitude } = pos.coords;
          mapRef.current?.flyTo([latitude, longitude], 15); // Zoom level 15
        },
        (error) => {
          // Handle geolocation errors silently to avoid logging NotAllowedError
          // Common errors: PERMISSION_DENIED (1), POSITION_UNAVAILABLE (2), TIMEOUT (3)
          if (!abortController.signal.aborted) {
            if (error.code === 1) {
              // Permission denied - this is expected if user denies location access
              window.alert('Location access is required to show your position on the map. Please allow location permission in your browser settings.');
            } else {
              console.warn('Geolocation error:', error.message);
            }
          }
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 },
      );
    };


    const zoomToPostcode = (postcode: string) => {
      if (!postcodeSlicePolygons) {
        console.warn('No postcode slice polygons available for zooming');
        return;
      }
      
      const bounds = calculatePostcodeBounds(postcodeSlicePolygons, postcode);
      if (bounds && mapRef.current) {
        // Convert bounds to Leaflet format and fly to the bounds
        const leafletBounds = [
          [bounds.latitude - bounds.latitudeDelta / 2, bounds.longitude - bounds.longitudeDelta / 2],
          [bounds.latitude + bounds.latitudeDelta / 2, bounds.longitude + bounds.longitudeDelta / 2]
        ];
        mapRef.current.flyToBounds(leafletBounds, { padding: [20, 20] });
      } else {
        console.warn('Could not calculate bounds for postcode:', postcode);
      }
    };

    useImperativeHandle(ref, () => ({
      centerOnUserLocation: centerOnUserLocation,
      zoomToPostcode: zoomToPostcode,
    }));

    // Component to handle zoom events and initial map state
    function ZoomHandler() {
      const map = useMapEvents({
        zoomend: (e) => {
          const currentZoom = e.target.getZoom();
          setShowMarkers(currentZoom >= MIN_ZOOM_FOR_MARKERS);
        },
      });

      // Check initial zoom level when component mounts
      React.useEffect(() => {
        if (map) {
          const currentZoom = map.getZoom();
          setShowMarkers(currentZoom >= MIN_ZOOM_FOR_MARKERS);
        }
      }, [map]);

      return null;
    }
    
    // UserLocation component that displays location with pulsing indicator
    function UserLocationMarker({ enabled }: { enabled: boolean }) {
      const [position, setPosition] = React.useState<[number, number] | null>(null);
      const [accuracy, setAccuracy] = React.useState<number | null>(null);
      const watchIdRef = React.useRef<number | null>(null);

      React.useEffect(() => {
        if (!enabled) {
          setPosition(null);
          setAccuracy(null);
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
          }
          return;
        }

        if (typeof window === 'undefined' || !navigator.geolocation) {
          return;
        }

        // Start watching position for continuous updates
        const watchId = navigator.geolocation.watchPosition(
          (pos) => {
            const { latitude, longitude, accuracy: posAccuracy } = pos.coords;
            setPosition([latitude, longitude]);
            setAccuracy(posAccuracy || null);
          },
          (error) => {
            // Handle geolocation errors silently
            // Common errors: PERMISSION_DENIED (1), POSITION_UNAVAILABLE (2), TIMEOUT (3)
            if (error.code === 1) {
              // Permission denied - user may have denied after initially allowing
              console.warn('Location permission denied');
            } else {
              console.warn('Geolocation error:', error.message);
            }
            setPosition(null);
            setAccuracy(null);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 1000, // Accept location up to 1 second old for better responsiveness
          }
        );

        watchIdRef.current = watchId;

        // Cleanup function
        return () => {
          if (watchIdRef.current !== null) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
          }
        };
      }, [enabled]);

      if (!position) return null;

      return (
        <>
          {/* Accuracy circle - limit max radius to 100m for visibility */}
          {accuracy && accuracy > 0 && accuracy < 1000 && (
            <Circle
              center={position}
              radius={Math.min(accuracy, 100)}
              pathOptions={{
                color: '#007AFF',
                fillColor: '#007AFF',
                fillOpacity: 0.1,
                weight: 1,
              }}
            />
          )}
          {/* User location dot - larger and more visible */}
          <CircleMarker
            center={position}
            radius={12}
            pathOptions={{
              color: '#ffffff',
              fillColor: '#007AFF',
              fillOpacity: 1,
              weight: 4,
            }}
          />
        </>
      );
    }
    
    // Component unmount cleanup
    React.useEffect(() => {
      return () => {
        if (geolocationAbortControllerRef.current) {
          geolocationAbortControllerRef.current.abort();
          geolocationAbortControllerRef.current = null;
        }
      };
    }, []);

    return (
      <div style={{ width: '100%', height: '100%', position: 'relative', ...style }}>
        <MapContainer
          ref={mapRef}
          bounds={bounds}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={true}
        >
          <ZoomHandler />
          {showUserLocation && <UserLocationMarker enabled={showUserLocation} />}
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
          />
          {polygons.map((polygon: MapPolygon) => (
            <Polygon
              key={polygon.id}
              positions={polygon.coordinates.map((c) => [c.latitude, c.longitude])}
              pathOptions={{
                color: polygon.strokeColor || '#007AFF',
                fillColor: polygon.fillColor || '#007AFF',
                weight: polygon.strokeWidth || 2,
                fillOpacity: 0.4, // Set a consistent fill opacity for web
              }}
              {...(polygon.onPress && { eventHandlers: { click: polygon.onPress } })}
            />
          ))}
          {showMarkers && markers.map((marker: MapMarker) => {
            const isRouteNumberMarker = marker.type === 'route-number';
            const isRouteNumberActiveMarker = marker.type === 'route-number-active';
            
            return (
              <Marker
                key={marker.id}
                position={[marker.coordinate.latitude, marker.coordinate.longitude]}
                eventHandlers={{ click: () => handleMarkerClick(marker) }}
                icon={L.divIcon({
                  className: isRouteNumberMarker || isRouteNumberActiveMarker ? 'route-number-marker' : 'campaigner-count-marker',
                  html: isRouteNumberMarker 
                    ? `<div style="display:flex;align-items:center;justify-content:center;min-width:28px;height:28px;border-radius:14px;background:#666666;color:#ffffff;font-weight:600;font-size:12px;text-align:center;border:2px solid #ffffff;padding:0 6px;">${marker.title || '?'}</div>`
                    : isRouteNumberActiveMarker
                    ? `<div style="display:flex;align-items:center;justify-content:center;min-width:28px;height:28px;border-radius:14px;background:#4CAF50;color:#ffffff;font-weight:600;font-size:12px;text-align:center;border:2px solid #ffffff;padding:0 6px;">${marker.title || '?'}</div>`
                    : `<div style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:16px;background:#28a745;color:#fff;font-weight:bold;font-size:14px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.15);">${marker.title || '?'}</div>`
                })}
              >
                {!isRouteNumberMarker && (
                  <Popup maxWidth={280} minWidth={200}>
                    <div style={{ padding: '8px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                      <div style={{ 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: '#333', 
                        marginBottom: '4px',
                        borderBottom: '1px solid #eee',
                        paddingBottom: '4px'
                      }}>
                        {marker.routeNumber}: {marker.routeName || 'Unnamed Route'}
                      </div>
                      <div style={{ 
                        fontSize: '14px', 
                        color: '#666', 
                        marginBottom: '8px' 
                      }}>
                        {marker.campaignerNames?.length || 0} Active Campaigner{(marker.campaignerNames?.length || 0) === 1 ? '' : 's'}
                      </div>
                      
                      {marker.campaignerNames && marker.campaignerNames.length > 0 && (
                        <div style={{ 
                          maxHeight: '120px', 
                          overflowY: 'auto',
                          marginTop: '8px'
                        }}>
                          {marker.campaignerNames
                            .filter((name): name is string => name !== null && name !== undefined)
                            .sort((a, b) => a.localeCompare(b))
                            .map((name: string, index: number) => (
                              <div key={index} style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                padding: '2px 4px',
                                fontSize: '13px',
                                color: '#333'
                              }}>
                                <span style={{ 
                                  marginRight: '6px', 
                                  color: '#666',
                                  fontSize: '12px'
                                }}>👤</span>
                                {name}
                              </div>
                            ))
                          }
                        </div>
                      )}
                    </div>
                  </Popup>
                )}
              </Marker>
            );
          })}
          {pollingStationMarkers.map((pollingStation: PollingStationMarker) => (
            <Marker
              key={pollingStation.id}
              position={[pollingStation.coordinate.latitude, pollingStation.coordinate.longitude]}
              eventHandlers={{ click: () => handlePollingStationClick(pollingStation) }}
              icon={L.divIcon({
                className: 'polling-station-marker',
                html: `<div style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:16px;background:#fff;color:#FF6B35;border:2px solid #FF6B35;box-shadow:0 2px 6px rgba(0,0,0,0.15);font-size:16px;">☑</div>`
              })}
            >
              <Popup maxWidth={280} minWidth={200}>
                <div style={{ padding: '8px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                  <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold', 
                    color: '#333', 
                    marginBottom: '4px',
                    borderBottom: '1px solid #eee',
                    paddingBottom: '4px'
                  }}>
                    Polling Station
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#666', 
                    fontWeight: '600'
                  }}>
                    {pollingStation.address}
                  </div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#666', 
                    fontWeight: '600',
                    marginTop: '4px'
                  }}>
                    {pollingStation.postcode}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
        {showUserLocation && (
          <button
            onClick={centerOnUserLocation}
            style={{
              position: 'absolute',
              bottom: 32,
              right: 24,
              backgroundColor: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 56,
              height: 56,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
              cursor: 'pointer',
              zIndex: 1000,
            }}
            aria-label="Show my location"
          >
            <MaterialIcons name="my-location" size={32} color="#007AFF" />
          </button>
        )}

        {/* Progress Legend */}
        {showProgressLegend && (
          showLegend ? (
            <div style={{
              position: 'absolute',
              top: 20,
              right: 20,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: 8,
              padding: 12,
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
              zIndex: 1000,
              minWidth: 120,
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 8 
              }}>
                <div style={{ fontSize: 14, fontWeight: 'bold' }}>Progress</div>
                <button
                  onClick={() => setShowLegend(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: 16,
                    color: '#666',
                  }}
                >
                  ✕
                </button>
              </div>
              
              <div style={{ marginBottom: 6 }}>
                {legendItems.map((item, index) => (
                  <div key={index} style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: 4 
                  }}>
                    <div style={{ 
                      width: 16, 
                      height: 16, 
                      backgroundColor: item.color, 
                      borderRadius: 2,
                      marginRight: 8
                    }} />
                    <div style={{ fontSize: 12 }}>{item.label}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowLegend(true)}
              style={{
                position: 'absolute',
                top: 20,
                right: 20,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: 32,
                height: 32,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                zIndex: 1000,
              }}
              aria-label="Show progress legend"
            >
              <MaterialIcons name="info-outline" size={18} color="#007AFF" />
            </button>
          )
        )}
      </div>
    );
  }

    const ForwardedLeafletMapInner = forwardRef(LeafletMapInner);
    return { default: ForwardedLeafletMapInner };
  })
);

export default forwardRef(function CrossPlatformMapView(
  props: MapViewProps,
  ref: React.Ref<MapViewRef>,
) {
  if (typeof window === 'undefined') {
    return <div style={{ width: '100%', height: '100%', ...props.style }} />;
  }
  return (
    <Suspense
      fallback={<div style={{ width: '100%', height: '100%', ...props.style }} />}
    >
      <LeafletMap {...props} ref={ref} />
    </Suspense>
  );
}); 